import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, User, CreditCard, FileText, Download, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth config with token from localStorage
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  };
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payments, setPayments] = useState([]);
  const [cards, setCards] = useState([]);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    whatsapp: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, paymentsRes, cardsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/profile`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/payments/history`, getAuthConfig()),
        axios.get(`${BACKEND_URL}/api/cards`, getAuthConfig())
      ]);
      
      setUser(profileRes.data);
      setProfileForm({
        name: profileRes.data.name || '',
        phone: profileRes.data.phone || '',
        whatsapp: profileRes.data.whatsapp || '',
        email: profileRes.data.email || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setPayments(paymentsRes.data || []);
      setCards(cardsRes.data || []);
    } catch (error) {
      toast.error('Gagal memuatkan data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    // Validate password if changing
    if (profileForm.new_password) {
      if (!profileForm.current_password) {
        toast.error('Sila masukkan kata laluan semasa');
        return;
      }
      if (profileForm.new_password !== profileForm.confirm_password) {
        toast.error('Kata laluan baharu tidak sepadan');
        return;
      }
      if (profileForm.new_password.length < 6) {
        toast.error('Kata laluan baharu mestilah sekurang-kurangnya 6 aksara');
        return;
      }
    }

    setSaving(true);
    try {
      const updateData = {
        name: profileForm.name,
        phone: profileForm.phone,
        whatsapp: profileForm.whatsapp,
        email: profileForm.email
      };

      if (profileForm.new_password) {
        updateData.current_password = profileForm.current_password;
        updateData.new_password = profileForm.new_password;
      }

      await axios.put(
        `${BACKEND_URL}/api/auth/profile`,
        updateData,
        getAuthConfig()
      );
      
      toast.success('Profil berjaya dikemaskini');
      setProfileForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengemaskini profil');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ms-MY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <span className="px-2 py-1 text-xs rounded-full bg-[#7678ED]/20 text-[#3D348B]">Berjaya</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs rounded-full bg-[#F7B801]/20 text-[#F18701]">Menunggu</span>;
      case 'failed':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Gagal</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D348B] mx-auto mb-4"></div>
          <p className="text-gray-600">Memuatkan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-[#3D348B] sticky top-0 z-40 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali
          </Button>
          <h1 className="text-xl font-bold text-white">Tetapan Akaun</h1>
          <div className="w-20"></div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Credit Balance Card */}
        <Card className="mb-8 bg-gradient-to-r from-[#3D348B] to-[#7678ED] text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Baki Kad Boleh Dicipta</p>
                <p className="text-4xl font-bold">
                  {user?.role === 'admin' ? '∞ Unlimited' : (user?.card_credits || 0)}
                </p>
              </div>
              <CreditCard className="w-12 h-12 text-[#F7B801]" />
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="cards" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Kad Saya
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Pembayaran
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Maklumat Profil</CardTitle>
                <CardDescription>Kemaskini maklumat akaun anda</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nama</Label>
                    <Input
                      id="name"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                      placeholder="Nama penuh"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">No. Telefon</Label>
                    <Input
                      id="phone"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                      placeholder="60123456789"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="whatsapp" className="flex items-center gap-1">
                    No. WhatsApp
                    <span className="text-red-500 text-xs">*wajib untuk pembayaran</span>
                  </Label>
                  <Input
                    id="whatsapp"
                    data-testid="whatsapp-input"
                    value={profileForm.whatsapp}
                    onChange={(e) => setProfileForm({...profileForm, whatsapp: e.target.value})}
                    placeholder="60123456789"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                    placeholder="email@contoh.com"
                  />
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-semibold mb-4">Tukar Kata Laluan</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current_password">Kata Laluan Semasa</Label>
                      <Input
                        id="current_password"
                        type="password"
                        value={profileForm.current_password}
                        onChange={(e) => setProfileForm({...profileForm, current_password: e.target.value})}
                        placeholder="••••••••"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new_password">Kata Laluan Baharu</Label>
                        <Input
                          id="new_password"
                          type="password"
                          value={profileForm.new_password}
                          onChange={(e) => setProfileForm({...profileForm, new_password: e.target.value})}
                          placeholder="••••••••"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm_password">Sahkan Kata Laluan</Label>
                        <Input
                          id="confirm_password"
                          type="password"
                          value={profileForm.confirm_password}
                          onChange={(e) => setProfileForm({...profileForm, confirm_password: e.target.value})}
                          placeholder="••••••••"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full"
                >
                  {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cards Tab */}
          <TabsContent value="cards">
            <Card>
              <CardHeader>
                <CardTitle>Senarai Kad</CardTitle>
                <CardDescription>Kad jemputan yang telah anda beli dan cipta</CardDescription>
              </CardHeader>
              <CardContent>
                {cards.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Tiada kad dijumpai</p>
                ) : (
                  <div className="space-y-4">
                    {cards.map((card) => (
                      <div
                        key={card.card_id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                        onClick={() => navigate(`/editor/${card.card_id}`)}
                      >
                        <div className="flex items-center gap-4">
                          {card.background_thumbnail && (
                            <img
                              src={card.background_thumbnail}
                              alt=""
                              className="w-16 h-12 object-cover rounded"
                            />
                          )}
                          <div>
                            <p className="font-semibold">{card.title || `${card.bride_name} & ${card.groom_name}`}</p>
                            <p className="text-sm text-gray-500">/{card.slug}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            card.status === 'published' ? 'bg-[#7678ED]/20 text-[#3D348B]' : 'bg-[#F7B801]/20 text-[#F18701]'
                          }`}>
                            {card.status === 'published' ? 'Diterbitkan' : 'Draf'}
                          </span>
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Sejarah Pembayaran</CardTitle>
                <CardDescription>Rekod transaksi pembelian kad</CardDescription>
              </CardHeader>
              <CardContent>
                {payments.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Tiada rekod pembayaran</p>
                ) : (
                  <div className="space-y-4">
                    {payments.map((payment) => (
                      <div
                        key={payment.payment_id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-semibold">{payment.quantity} Kad</p>
                          <p className="text-sm text-gray-500">{payment.order_number}</p>
                          <p className="text-xs text-gray-400">{formatDate(payment.created_at)}</p>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            <p className="font-bold text-[#7678ED]">RM{payment.amount}</p>
                            {getStatusBadge(payment.status)}
                          </div>
                          {payment.status === 'success' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                // Open PDF in new tab for download
                                window.open(`${BACKEND_URL}/api/payments/${payment.payment_id}/invoice/pdf`, '_blank');
                              }}
                              title="Muat turun Invois PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
